import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import InstallerTable from "@/pages/admin/components/InstallerTable";

const NAV = [
  { path: "/admin", label: "Overview", icon: "ri-dashboard-line", exact: true },
  { path: "/admin/leads", label: "Manage Leads", icon: "ri-list-check-2" },
  { path: "/admin/installers", label: "Installers", icon: "ri-team-line" },
  { path: "/admin/purchases", label: "Purchases", icon: "ri-shopping-bag-line" },
];

function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);
  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <header className="bg-gray-900 px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-[#16a34a] rounded-md flex items-center justify-center">
            <i className="ri-sun-line text-white text-xs"></i>
          </div>
          <span className="font-bold text-white text-base">
            SolarLead<span className="text-[#16a34a]">Source</span>{" "}
            <span className="text-gray-400 font-normal text-sm">Admin</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">ADMIN</span>
          <Link to="/login" className="text-xs text-gray-400 hover:text-white transition whitespace-nowrap">
            Sign out
          </Link>
        </div>
      </header>
      <div className="flex flex-1">
        <aside className="w-56 bg-white border-r border-gray-100 hidden md:flex flex-col py-4">
          <nav className="px-3 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition cursor-pointer ${isActive(item.path, item.exact) ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              >
                <i className={`${item.icon} text-base`}></i>{item.label}
              </Link>
            ))}
          </nav>
        </aside>
        <main className="flex-1 p-6 md:p-8">{children}</main>
      </div>
    </div>
  );
}

// ─── OVERVIEW ────────────────────────────────────────────────────────────────
function Overview() {
  const [stats, setStats] = useState({ available: 0, sold: 0, revenue: 0, installers: 0 });
  const [recentPurchases, setRecentPurchases] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [leadsRes, purchasesRes, installersRes] = await Promise.all([
        supabase.from("leads").select("status"),
        supabase.from("lead_purchases").select("amount, created_at, leads(city, state), installers(company)").eq("status", "completed").order("created_at", { ascending: false }).limit(10),
        supabase.from("installers").select("contract_signed"),
      ]);

      const leads = leadsRes.data || [];
      const purchases = purchasesRes.data || [];
      const installers = installersRes.data || [];

      setStats({
        available: leads.filter((l: Record<string, unknown>) => l.status === "available").length,
        sold: leads.filter((l: Record<string, unknown>) => l.status === "sold").length,
        revenue: purchases.reduce((s: number, p: Record<string, unknown>) => s + Number(p.amount), 0),
        installers: installers.filter((i: Record<string, unknown>) => i.contract_signed).length,
      });
      setRecentPurchases(purchases);
      setLoading(false);
    };
    load();
  }, []);

  const statCards = [
    { label: "Available Leads", value: stats.available, icon: "ri-store-2-line", color: "text-[#16a34a]" },
    { label: "Leads Sold", value: stats.sold, icon: "ri-shopping-bag-line", color: "text-gray-900" },
    { label: "Total Revenue", value: `$${stats.revenue.toLocaleString()}`, icon: "ri-money-dollar-circle-line", color: "text-[#16a34a]" },
    { label: "Active Installers", value: stats.installers, icon: "ri-team-line", color: "text-gray-900" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Admin Overview</h1>
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 h-24 animate-pulse"></div>)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center mb-3">
                <i className={`${s.icon} text-lg text-gray-600`}></i>
              </div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}
      <div className="bg-white border border-gray-100 rounded-2xl p-6">
        <h2 className="text-base font-bold text-gray-900 mb-4">Recent Purchases</h2>
        {recentPurchases.length === 0 ? (
          <p className="text-sm text-gray-400">No purchases yet.</p>
        ) : (
          <div className="space-y-3">
            {recentPurchases.map((p: Record<string, unknown>, i) => {
              const lead = p.leads as Record<string, unknown>;
              const installer = p.installers as Record<string, unknown>;
              return (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 text-sm">
                  <div>
                    <span className="text-gray-700 font-medium">{lead?.city}, {lead?.state}</span>
                    <span className="text-gray-400 text-xs ml-2">· {installer?.company}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 text-xs">{new Date(p.created_at as string).toLocaleDateString()}</span>
                    <span className="font-bold text-gray-900">${p.amount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MANAGE LEADS ─────────────────────────────────────────────────────────────
function ManageLeads() {
  const [leads, setLeads] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [newLead, setNewLead] = useState({
    state: "", city: "", zip: "", bill_range: "",
    solar_score: "85", lead_score: "80", timeline: "1–3 months",
    price: "150", name: "", phone: "", address: "", notes: "",
  });

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("leads")
      .select("id, name, email, phone, address, state, city, zip, bill_range, lead_score, solar_score, tier, timeline, price, status, is_premium, monthly_bill, annual_savings, system_size, panel_count, federal_incentive, payback_period, co2_offset, sunshine_hours, homeowner, roof_condition, assigned_to, date_submitted, created_at")
      .order("created_at", { ascending: false });
    setLeads(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const addLead = async () => {
    if (!newLead.state || !newLead.city) return;
    setSaving(true);
    const { error } = await supabase.from("leads").insert({
      state: newLead.state,
      city: newLead.city,
      zip: newLead.zip,
      bill_range: newLead.bill_range,
      solar_score: Number(newLead.solar_score),
      lead_score: Number(newLead.lead_score),
      timeline: newLead.timeline,
      price: Number(newLead.price),
      name: newLead.name,
      phone: newLead.phone,
      address: newLead.address,
      notes: newLead.notes,
      status: "available",
      is_premium: false,
    });
    setSaving(false);
    if (!error) {
      setShowForm(false);
      setNewLead({ state: "", city: "", zip: "", bill_range: "", solar_score: "85", lead_score: "80", timeline: "1–3 months", price: "150", name: "", phone: "", address: "", notes: "" });
      setSuccessMsg("Lead added successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
      fetchLeads();
    }
  };

  const togglePremium = async (id: string, current: boolean) => {
    await supabase.from("leads").update({ is_premium: !current }).eq("id", id);
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, is_premium: !current } : l));
  };

  const handleCsvSuccess = (count: number) => {
    setShowCsvUpload(false);
    setSuccessMsg(`${count} lead${count !== 1 ? "s" : ""} imported successfully!`);
    setTimeout(() => setSuccessMsg(""), 4000);
    fetchLeads();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Manage Leads</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowCsvUpload(!showCsvUpload); setShowForm(false); }}
            className="border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-upload-cloud-2-line"></i>Upload CSV
          </button>
          <button
            onClick={() => { setShowForm(!showForm); setShowCsvUpload(false); }}
            className="bg-[#16a34a] hover:bg-[#15803d] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line"></i>Add Lead
          </button>
        </div>
      </div>

      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
          <i className="ri-check-line"></i>{successMsg}
        </div>
      )}

      {showCsvUpload && (
        <CsvUploadPanel onSuccess={handleCsvSuccess} onCancel={() => setShowCsvUpload(false)} />
      )}

      {showForm && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-4">
          <h2 className="font-bold text-gray-900">New Lead</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { key: "state", label: "State *", placeholder: "California" },
              { key: "city", label: "City *", placeholder: "San Diego" },
              { key: "zip", label: "ZIP", placeholder: "92101" },
              { key: "bill_range", label: "Bill Range", placeholder: "$200–$300" },
              { key: "timeline", label: "Timeline", placeholder: "1–3 months" },
              { key: "price", label: "Price ($)", placeholder: "150" },
              { key: "lead_score", label: "Lead Score", placeholder: "80" },
              { key: "solar_score", label: "Solar Score", placeholder: "85" },
              { key: "name", label: "Homeowner Name", placeholder: "John Doe" },
              { key: "phone", label: "Phone", placeholder: "(555) 000-0000" },
              { key: "address", label: "Address", placeholder: "123 Main St" },
              { key: "notes", label: "Notes", placeholder: "Additional info..." },
            ].map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <input
                  value={(newLead as Record<string, string>)[f.key]}
                  onChange={(e) => setNewLead((p) => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowForm(false)}
              className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={addLead}
              disabled={saving}
              className="bg-[#16a34a] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#15803d] transition cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {saving ? <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Saving...</span> : "Save Lead"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {["Name", "Contact", "Location", "Monthly Bill", "Annual Savings", "System", "Lead Score", "Tier", "Roof", "Homeowner", "Timeline", "Price", "Status", "Premium"].map((h) => (
                  <th key={h} className="text-left text-xs text-gray-400 font-medium px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(14)].map((__, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"></div></td>
                    ))}
                  </tr>
                ))
              ) : leads.map((l) => (
                <tr key={l.id as string} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 whitespace-nowrap">{(l.name as string) || "—"}</p>
                    <p className="text-xs text-gray-400">{(l.email as string) || ""}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-700 whitespace-nowrap">{(l.phone as string) || "—"}</p>
                    <p className="text-xs text-gray-400 max-w-[140px] truncate">{(l.address as string) || ""}</p>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{l.city as string}, {l.state as string} {l.zip ? <span className="text-gray-400 font-normal text-xs">{l.zip as string}</span> : null}</td>
                  <td className="px-4 py-3 text-gray-700">{l.monthly_bill ? `$${l.monthly_bill}` : (l.bill_range as string) || "—"}</td>
                  <td className="px-4 py-3 text-[#16a34a] font-semibold whitespace-nowrap">{l.annual_savings ? `$${Number(l.annual_savings).toLocaleString()}` : "—"}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {l.system_size ? <span>{l.system_size as number} kW</span> : "—"}
                    {l.panel_count ? <span className="text-xs text-gray-400 ml-1">· {l.panel_count as number} panels</span> : null}
                  </td>
                  <td className="px-4 py-3"><span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">{(l.lead_score as number) || "—"}</span></td>
                  <td className="px-4 py-3">
                    {l.tier ? <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full capitalize">{l.tier as string}</span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">{(l.roof_condition as string) || "—"}</td>
                  <td className="px-4 py-3">
                    {l.homeowner === true
                      ? <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">Yes</span>
                      : l.homeowner === false
                      ? <span className="bg-red-100 text-red-500 text-xs font-bold px-2 py-0.5 rounded-full">No</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{(l.timeline as string) || "—"}</td>
                  <td className="px-4 py-3 font-bold text-gray-900">${l.price as number}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap ${l.status === "available" ? "bg-green-100 text-green-700" : l.status === "sold" ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-700"}`}>
                      {l.status as string}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => togglePremium(l.id as string, l.is_premium as boolean)}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer transition whitespace-nowrap ${l.is_premium ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                    >
                      {l.is_premium ? "Premium" : "Standard"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── CSV UPLOAD PANEL ─────────────────────────────────────────────────────────
const CSV_COLUMNS = [
  "name", "email", "phone", "address", "state", "city", "zip",
  "lead_score", "tier", "status", "assigned_to", "date_submitted",
  "monthly_bill", "annual_savings", "system_size", "panel_count",
  "federal_incentive", "payback_period", "co2_offset", "sunshine_hours",
  "homeowner", "roof_condition",
  "bill_range", "solar_score", "timeline", "price", "notes",
] as const;
type CsvColumn = typeof CSV_COLUMNS[number];

interface ParsedRow {
  name: string;
  email: string;
  phone: string;
  address: string;
  state: string;
  city: string;
  zip: string;
  lead_score: number;
  tier: string;
  status: string;
  assigned_to: string;
  date_submitted: string;
  monthly_bill: number;
  annual_savings: number;
  system_size: number;
  panel_count: number;
  federal_incentive: number;
  payback_period: number;
  co2_offset: number;
  sunshine_hours: number;
  homeowner: boolean;
  roof_condition: string;
  bill_range: string;
  solar_score: number;
  timeline: string;
  price: number;
  notes: string;
}

function CsvUploadPanel({ onSuccess, onCancel }: { onSuccess: (count: number) => void; onCancel: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, CsvColumn | "">>({});
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [preview, setPreview] = useState<Record<string, string>[]>([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"upload" | "map" | "confirm">("upload");

  const parseCSV = (text: string): string[][] => {
    const lines = text.trim().split(/\r?\n/);
    return lines.map((line) => {
      const result: string[] = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === "," && !inQuotes) { result.push(current.trim()); current = ""; }
        else { current += ch; }
      }
      result.push(current.trim());
      return result;
    });
  };

  const autoMap = (hdrs: string[]): Record<string, CsvColumn | ""> => {
    const aliases: Record<string, CsvColumn> = {
      email: "email",
      phone: "phone",
      mobile: "phone",
      address: "address",
      street: "address",
      state: "state",
      city: "city",
      zip: "zip",
      zipcode: "zip",
      postal: "zip",
      name: "name",
      contact: "name",
      full_name: "name",
      lead_score: "lead_score",
      score: "lead_score",
      tier: "tier",
      status: "status",
      assigned_to: "assigned_to",
      assignee: "assigned_to",
      date_submitted: "date_submitted",
      submitted: "date_submitted",
      date: "date_submitted",
      monthly_bill: "monthly_bill",
      monthly_electric_bill: "monthly_bill",
      bill: "monthly_bill",
      annual_savings: "annual_savings",
      savings: "annual_savings",
      system_size: "system_size",
      system_size_kw: "system_size",
      panel_count: "panel_count",
      panels: "panel_count",
      federal_incentive: "federal_incentive",
      incentive: "federal_incentive",
      payback_period: "payback_period",
      payback: "payback_period",
      co2_offset: "co2_offset",
      co2: "co2_offset",
      sunshine_hours: "sunshine_hours",
      sunshine: "sunshine_hours",
      homeowner: "homeowner",
      is_homeowner: "homeowner",
      roof_condition: "roof_condition",
      roof: "roof_condition",
      bill_range: "bill_range",
      solar_score: "solar_score",
      timeline: "timeline",
      price: "price",
      notes: "notes",
    };
    const m: Record<string, CsvColumn | ""> = {};
    hdrs.forEach((h) => {
      const key = h.toLowerCase().replace(/[^a-z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      m[h] = aliases[key] || CSV_COLUMNS.find((col) => key.includes(col) || col.includes(key)) || "";
    });
    return m;
  };

  const processFile = (file: File) => {
    if (!file.name.endsWith(".csv")) { setError("Please upload a .csv file."); return; }
    setError("");
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length < 2) { setError("CSV must have at least a header row and one data row."); return; }
      const hdrs = rows[0];
      const dataRows = rows.slice(1).filter((r) => r.some((c) => c !== ""));
      setHeaders(hdrs);
      setRawRows(dataRows);
      setMapping(autoMap(hdrs));
      setStep("map");
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const buildPreview = () => {
    setError("");
    
    // Only State is required; City is optional
    const stateHeader = headers.find((h) => mapping[h] === "state");

    if (!stateHeader) {
      setError("Please map the State column before previewing.");
      return;
    }

    const stateIdx = headers.indexOf(stateHeader);

    const validRows = rawRows.filter((row) => row[stateIdx]?.trim());

    if (validRows.length === 0) {
      setError(`No valid rows found. Found ${rawRows.length} total rows, but none have a State value. Check your CSV data.`);
      return;
    }

    const rows = validRows.slice(0, 5).map((row) => {
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => {
        if (mapping[h]) obj[mapping[h]] = row[i] || "";
      });
      return obj;
    });
    setPreview(rows);
    setStep("confirm");
  };

  const handleImport = async () => {
    setImporting(true);
    setError("");
    const toInsert = rawRows.map((row) => {
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => { if (mapping[h]) obj[mapping[h]] = row[i] || ""; });
      const homeownerRaw = obj.homeowner?.toLowerCase();
      return {
        name: obj.name || "",
        email: obj.email || "",
        phone: obj.phone || "",
        address: obj.address || "",
        state: obj.state || "",
        city: obj.city || "",
        zip: obj.zip || "",
        lead_score: obj.lead_score ? Number(obj.lead_score) : null,
        tier: obj.tier || null,
        status: obj.status || "available",
        assigned_to: obj.assigned_to || null,
        date_submitted: obj.date_submitted || null,
        monthly_bill: obj.monthly_bill ? Number(obj.monthly_bill) : null,
        annual_savings: obj.annual_savings ? Number(obj.annual_savings) : null,
        system_size: obj.system_size ? Number(obj.system_size) : null,
        panel_count: obj.panel_count ? Number(obj.panel_count) : null,
        federal_incentive: obj.federal_incentive ? Number(obj.federal_incentive) : null,
        payback_period: obj.payback_period ? Number(obj.payback_period) : null,
        co2_offset: obj.co2_offset ? Number(obj.co2_offset) : null,
        sunshine_hours: obj.sunshine_hours ? Number(obj.sunshine_hours) : null,
        homeowner: homeownerRaw === "yes" || homeownerRaw === "true" || homeownerRaw === "1" ? true : homeownerRaw === "no" || homeownerRaw === "false" || homeownerRaw === "0" ? false : null,
        roof_condition: obj.roof_condition || null,
        bill_range: obj.bill_range || null,
        solar_score: obj.solar_score ? Number(obj.solar_score) : null,
        timeline: obj.timeline || null,
        price: obj.price ? Number(obj.price) : 150,
        notes: obj.notes || "",
        is_premium: false,
      };
    }).filter((r) => r.state);

    if (toInsert.length === 0) {
      setError("No valid rows found. Make sure state and city columns are mapped.");
      setImporting(false);
      return;
    }

    // Insert in batches of 50
    const batchSize = 50;
    let inserted = 0;
    for (let i = 0; i < toInsert.length; i += batchSize) {
      const batch = toInsert.slice(i, i + batchSize);
      const { error: err } = await supabase.from("leads").insert(batch as unknown as Record<string, unknown>[]);
      if (err) { setError(`Import failed: ${err.message}`); setImporting(false); return; }
      inserted += batch.length;
    }
    setImporting(false);
    onSuccess(inserted);
  };

  const COLUMN_LABELS: Record<CsvColumn, string> = {
    name: "Name", email: "Email", phone: "Phone", address: "Address",
    state: "State", city: "City", zip: "ZIP",
    lead_score: "Lead Score", tier: "Tier", status: "Status",
    assigned_to: "Assigned To", date_submitted: "Date Submitted",
    monthly_bill: "Monthly Bill ($)", annual_savings: "Annual Savings ($)",
    system_size: "System Size (kW)", panel_count: "Panel Count",
    federal_incentive: "Federal Incentive ($)", payback_period: "Payback Period (yrs)",
    co2_offset: "CO₂ Offset (tons)", sunshine_hours: "Sunshine Hours",
    homeowner: "Homeowner", roof_condition: "Roof Condition",
    bill_range: "Bill Range", solar_score: "Solar Score",
    timeline: "Timeline", price: "Price ($)", notes: "Notes",
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-gray-900">Import Leads via CSV</h2>
          <p className="text-xs text-gray-400 mt-0.5">Upload a CSV file to bulk-import leads. State column is required.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {["Upload", "Map Columns", "Confirm"].map((s, i) => (
            <span key={s} className="flex items-center gap-1">
              {i > 0 && <span className="text-gray-200">›</span>}
              <span className={`font-medium ${step === ["upload","map","confirm"][i] ? "text-[#16a34a]" : "text-gray-300"}`}>{s}</span>
            </span>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 text-sm px-4 py-3 rounded-lg flex items-start gap-2">
          <i className="ri-error-warning-fill text-red-500 mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="font-semibold">Import Error</p>
            <p className="mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: Upload */}
      {step === "upload" && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ${dragging ? "border-[#16a34a] bg-green-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
        >
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <i className="ri-file-text-line text-2xl text-gray-400"></i>
          </div>
          <p className="text-sm font-medium text-gray-700">Drag & drop your CSV file here</p>
          <p className="text-xs text-gray-400 mt-1">or click to browse</p>
          <p className="text-xs text-gray-300 mt-3">Supported format: .csv</p>
        </div>
      )}

      {/* Step 2: Map Columns */}
      {step === "map" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2.5">
            <i className="ri-file-text-line text-gray-400"></i>
            <span className="font-medium text-gray-700">{fileName}</span>
            <span className="text-gray-400">· {rawRows.length} rows detected</span>
          </div>
          <p className="text-sm text-gray-600">Map your CSV columns to lead fields. <span className="text-[#16a34a] font-medium">State</span> is required. City is optional.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {headers.map((h) => (
              <div key={h} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2.5">
                <span className="text-xs font-mono text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded min-w-0 flex-1 truncate">{h}</span>
                <i className="ri-arrow-right-line text-gray-300 flex-shrink-0"></i>
                <select
                  value={mapping[h]}
                  onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value as CsvColumn | "" }))}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-green-500 flex-shrink-0 w-36"
                >
                  <option value="">— Skip —</option>
                  {CSV_COLUMNS.map((col) => (
                    <option key={col} value={col}>{COLUMN_LABELS[col]}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {/* Mapping status indicators */}
          <div className="flex items-center gap-3 text-xs">
            <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full font-medium ${headers.some((h) => mapping[h] === "state") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
              <i className={headers.some((h) => mapping[h] === "state") ? "ri-check-line" : "ri-close-line"}></i>
              State {headers.some((h) => mapping[h] === "state") ? "mapped" : "not mapped — required"}
            </span>
            <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full font-medium ${headers.some((h) => mapping[h] === "city") ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
              <i className={headers.some((h) => mapping[h] === "city") ? "ri-check-line" : "ri-subtract-line"}></i>
              City {headers.some((h) => mapping[h] === "city") ? "mapped" : "optional"}
            </span>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep("upload")} className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
              Back
            </button>
            <button
              onClick={buildPreview}
              disabled={!headers.some((h) => mapping[h] === "state")}
              className="bg-[#16a34a] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#15803d] transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Preview Import
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === "confirm" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <i className="ri-checkbox-circle-line text-[#16a34a]"></i>
            <span className="text-gray-700 font-medium">{(() => {
              const sh = headers.find((h) => mapping[h] === "state");
              if (!sh) return 0;
              const si = headers.indexOf(sh);
              return rawRows.filter((row) => row[si]?.trim()).length;
            })()} leads ready to import</span>
            <span className="text-gray-400 text-xs">(showing first 5 below)</span>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {(Object.values(mapping).filter(Boolean) as CsvColumn[]).map((col) => (
                    <th key={col} className="text-left text-gray-400 font-medium px-3 py-2">{COLUMN_LABELS[col]}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {preview.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {(Object.values(mapping).filter(Boolean) as CsvColumn[]).map((col) => (
                      <td key={col} className="px-3 py-2 text-gray-700 max-w-[120px] truncate">{row[col] || "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep("map")} className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
              Back
            </button>
            <button onClick={onCancel} className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={importing}
              className="bg-[#16a34a] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#15803d] transition cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
            >
              {importing ? <><i className="ri-loader-4-line animate-spin"></i>Importing...</> : <><i className="ri-upload-cloud-2-line"></i>Import {(() => { const sh = headers.find((h) => mapping[h] === "state"); if (!sh) return 0; const si = headers.indexOf(sh); return rawRows.filter((row) => row[si]?.trim()).length; })()} Leads</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── INSTALLERS ───────────────────────────────────────────────────────────────
function Installers() {
  return <InstallerTable />;
}

// ─── PURCHASES ────────────────────────────────────────────────────────────────
function Purchases() {
  const [purchases, setPurchases] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState<string | null>(null);

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("lead_purchases")
      .select(`id, amount, status, created_at, stripe_session_id, leads(city, state), installers(company)`)
      .order("created_at", { ascending: false });
    setPurchases(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPurchases(); }, [fetchPurchases]);

  const handleRefund = async (purchaseId: string, leadId: string) => {
    setRefunding(purchaseId);
    // Mark purchase as refunded
    await supabase.from("lead_purchases").update({ status: "refunded" }).eq("id", purchaseId);
    // Release lead back to available
    await supabase.from("leads").update({
      status: "available",
      assigned_installer_id: null,
      reserved_by: null,
      reserved_until: null,
    }).eq("id", leadId);
    setRefunding(null);
    fetchPurchases();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">All Purchases</h1>
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {["Date", "Lead", "Installer", "Amount", "Status", "Actions"].map((h) => (
                  <th key={h} className="text-left text-xs text-gray-400 font-medium px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(6)].map((__, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"></div></td>
                    ))}
                  </tr>
                ))
              ) : purchases.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">No purchases yet.</td></tr>
              ) : purchases.map((p) => {
                const lead = p.leads as Record<string, unknown>;
                const installer = p.installers as Record<string, unknown>;
                return (
                  <tr key={p.id as string} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-gray-500">{new Date(p.created_at as string).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{lead?.city}, {lead?.state}</td>
                    <td className="px-4 py-3 text-gray-600">{installer?.company}</td>
                    <td className="px-4 py-3 font-bold text-gray-900">${p.amount as number}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${p.status === "completed" ? "bg-green-100 text-green-700" : p.status === "refunded" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                        {p.status as string}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {p.status === "completed" && (
                        <button
                          onClick={() => handleRefund(p.id as string, (lead as Record<string, unknown>)?.id as string)}
                          disabled={refunding === p.id}
                          className="text-xs text-red-500 hover:underline cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {refunding === p.id ? "Refunding..." : "Refund"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const location = useLocation();
  const path = location.pathname;
  return (
    <AdminLayout>
      {path === "/admin" && <Overview />}
      {path === "/admin/leads" && <ManageLeads />}
      {path === "/admin/installers" && <Installers />}
      {path === "/admin/purchases" && <Purchases />}
    </AdminLayout>
  );
}
